import { CmdModal, CmdModalFooterButton } from '@commander-services/gui-components';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import Comment from './Comment';
import Tag from './Tag';
import InfoTable from './InfoTable';
import { IInfoParkingTag, IInfoRecord, IInfoRideTag } from '../interfaces';
import { ALARM_ID, EVENT_ID, WAYPOINT_ID } from '../constants';
import { rideInfoRecordsAtom } from '../TripsOverviewState';
import {
  getCommentEditPermission,
  getCommentRemovePermisson,
  getInitPage,
  getUsageRemovePermission,
  loadFirstEvents,
} from '../TripsOverviewService';
import useAnalytics from '../../../hooks/useAnalytics';

interface IInfoTableModalProps {
  rideId: string;
  customerId: number;
  cancelCallback: () => void;
  recordsCount: number;
  locked: string;
  rideComment: string | null;
  parkingComment: string | null;
  rideTags: IInfoRideTag[];
  parkingTags: IInfoParkingTag[];
  summary: { [key: string]: number };
  start: string;
  isAnonym: boolean;
}

const TAG_ID = 'tag';
const COMMENT_ID = 'comment';
const ESCAPE_CODE = 'Escape';
const KEYDOWN_CODE = 'keydown';

function Loading(props: { text: string }) {
  return (
    <tbody>
      <tr>
        <td
          style={{
            display: 'flex',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
            width: '100%',
            background: '#fff',
          }}
        >
          <h6
            className=" text-black"
            style={{
              fontWeight: 'bold',
            }}
          >
            {props.text}
          </h6>
        </td>
      </tr>
    </tbody>
  );
}

function InfoTableModal(props: IInfoTableModalProps) {
  const { formatMessage: f } = useIntl();
  const rideInfoRecords = useRecoilValue<IInfoRecord[]>(rideInfoRecordsAtom(props.rideId));
  const [modalRecords, setModalRecords] = React.useState<IInfoRecord[]>(rideInfoRecords);
  const [filters, setFilters] = React.useState<string[]>([]);
  const [information, setInformation] = React.useState<string>('');
  const [recordsCount, setRecordsCount] = React.useState<number>(props.recordsCount);
  const [currentPage, setCurrentPage] = React.useState<number>(getInitPage(rideInfoRecords.length));
  const [loader, setLoader] = React.useState(false);
  const { trackEvent } = useAnalytics();

  const handleCancel = () => {
    props.cancelCallback();
    trackEvent(['ride book', 'click', 'Ride book info table modal close']);
  };

  const dialogRoot = document.getElementById('modal-root');

  const handleEscapeKey = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.code === ESCAPE_CODE && dialogRoot) {
        props.cancelCallback();
      }
    },
    [dialogRoot, props]
  );

  React.useEffect(() => {
    window.addEventListener(KEYDOWN_CODE, handleEscapeKey);
    return () => {
      window.removeEventListener(KEYDOWN_CODE, handleEscapeKey);
    };
  }, [handleEscapeKey]);

  const handleEventsAfterFilterChanging = async (
    informationValue: string,
    filtersValue: string[]
  ) => {
    setLoader(true);
    const result = await loadFirstEvents(
      props.rideId,
      String(props.customerId),
      informationValue,
      filtersValue
    );
    setLoader(false);
    if (result) {
      setModalRecords(result.records);
      setRecordsCount(result.recordsCount);
      setCurrentPage(1);
    }
  };

  const resetTable = (informationValue: string, filtersValue: string[]) => {
    setModalRecords([]);
    setCurrentPage(0);
    setRecordsCount(0);
    if (
      !filtersValue.includes(ALARM_ID) ||
      !filtersValue.includes(WAYPOINT_ID) ||
      !filtersValue.includes(EVENT_ID)
    ) {
      handleEventsAfterFilterChanging(informationValue, filtersValue);
    }
  };

  const handleFilters = (event: React.ChangeEvent<HTMLInputElement>) => {
    const type = event.currentTarget.id;
    if (type) {
      trackEvent(['ride book', 'click', `Ride book info table modal filter: ${type}`]);
      const filterByCheckbox = filters;
      const filterIndex = filterByCheckbox.indexOf(type);

      if (filterIndex > -1) {
        const newFilters = filterByCheckbox.filter((filterType: string) => filterType !== type);
        setFilters(newFilters);
        if (type !== COMMENT_ID && type !== TAG_ID) {
          resetTable(information, newFilters);
        }
      } else {
        const newFilters = [...filterByCheckbox, type];
        setFilters(newFilters);
        if (type !== COMMENT_ID && type !== TAG_ID) {
          resetTable(information, newFilters);
        }
      }
    }
  };

  const getSummaryResult = (type: string) => {
    if (String(props.summary[type]) === '--') {
      return <>&ndash;</>;
    }
    return props.summary[type];
  };

  const handleCheckBoxCount = (type: string): number | JSX.Element => {
    return props.summary ? getSummaryResult(type) : 0;
  };

  const getRideComment = () => {
    if (!filters.includes('comment') && information !== 'parking') {
      return props.rideComment;
    }
    return null;
  };

  const getParkingComment = () => {
    if (!filters.includes('comment') && information !== 'ride') {
      return props.parkingComment;
    }
    return null;
  };

  const getRideTags = () => {
    if (!filters.includes('tag') && information !== 'parking') {
      return props.rideTags;
    }
    return null;
  };

  const getParkingTags = () => {
    if (!filters.includes('tag') && information !== 'ride') {
      return props.rideTags;
    }
    return null;
  };

  interface IFilterInformationEvent extends React.MouseEvent<HTMLInputElement> {
    currentTarget: HTMLInputElement & {
      getAttribute(attributeName: string): string | null;
    };
  }

  const filterInformation = (event: IFilterInformationEvent) => {
    const id = event.currentTarget.getAttribute('data-id');
    if (id) {
      trackEvent(['ride book', 'click', `Ride book info table modal filter: ${id}`]);
      setInformation(id === 'all' ? '' : id);
      resetTable(id === 'all' ? '' : id, filters);
    }
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'alarms.modal.rule.button.close' }),
      closeCallback: handleCancel,
    },
  ];
  return (
    <CmdModal
      id="rides-info"
      title={f({ id: 'table.rideBook.detail.row.modal.title' })}
      footerButtons={buttons}
      width="907px"
    >
      <div className="row mb-5">
        <label className="col-12 col-lg-2 form-label">
          <strong>{f({ id: 'table.rideBook.detail.row.modal.information' })}</strong>
        </label>
        <div className="col-auto px-5">
          <div className="e-radio font-size-sm">
            <label>
              <input
                onClick={filterInformation}
                data-id="ride"
                type="radio"
                name="info"
                data-cy="ride-book-rides-info-rides"
              />
              <span>{f({ id: 'table.rideBook.modal.ride' })}</span>
            </label>
          </div>
        </div>
        <div className="col-auto px-5">
          <div className="e-radio font-size-sm">
            <label>
              <input
                type="radio"
                name="info"
                onClick={filterInformation}
                data-id="parking"
                data-cy="ride-book-rides-info-parkings"
              />
              <span>{f({ id: 'table.rideBook.modal.parking' })}</span>
            </label>
          </div>
        </div>
        <div className="col-auto px-5">
          <div className="e-radio font-size-sm">
            <label>
              <input
                type="radio"
                name="info"
                onClick={filterInformation}
                data-id="all"
                defaultChecked={true}
                data-cy="ride-book-rides-info-ride-and-parking"
              />
              <span>{f({ id: 'table.rideBook.modal.rideAndParking' })}</span>
            </label>
          </div>
        </div>
      </div>

      <div className="row mb-5">
        <label className="col-12 col-lg-2 form-label">
          <strong>{f({ id: 'table.rideBook.detail.row.modal.type' })}</strong>
        </label>
        <div className="col-6 col-md-auto px-5">
          <div className="e-checkbox font-size-sm">
            <label htmlFor={WAYPOINT_ID}>
              <input
                id={WAYPOINT_ID}
                type="checkbox"
                className="mr-1"
                onChange={handleFilters}
                defaultChecked={true}
                data-cy="ride-book-rides-info-waypoints"
              />
              <span>
                {f({ id: 'tripsOverview.modal.waypoint' })} ({handleCheckBoxCount(WAYPOINT_ID)})
              </span>
            </label>
          </div>
        </div>
        <div className="col-6 col-md-auto px-5">
          <div className="e-checkbox font-size-sm">
            <label htmlFor={EVENT_ID}>
              <input
                id={EVENT_ID}
                type="checkbox"
                className="mr-1"
                onChange={handleFilters}
                defaultChecked={true}
                data-cy="ride-book-rides-info-events"
              />
              <span>
                {f({ id: 'tripsOverview.modal.event' })} ({handleCheckBoxCount(EVENT_ID)})
              </span>
            </label>
          </div>
        </div>
        <div className="col-6 col-md-auto px-5">
          <div className="e-checkbox font-size-sm">
            <label htmlFor={ALARM_ID}>
              <input
                id={ALARM_ID}
                type="checkbox"
                className="mr-1"
                onChange={handleFilters}
                defaultChecked={true}
                data-cy="ride-book-rides-info-alarms"
              />
              <span>
                {f({ id: 'tripsOverview.modal.alarm' })} ({handleCheckBoxCount(ALARM_ID)})
              </span>
            </label>
          </div>
        </div>
        <div className="col-6 col-md-auto px-5">
          <div className="e-checkbox font-size-sm">
            <label htmlFor={TAG_ID}>
              <input
                id={TAG_ID}
                type="checkbox"
                className="mr-1"
                onChange={handleFilters}
                defaultChecked={true}
                data-cy="ride-book-rides-info-tags"
              />
              <span>
                {f({ id: 'tripsOverview.modal.tag' })} ({handleCheckBoxCount(TAG_ID)})
              </span>
            </label>
          </div>
        </div>
        <div className="col-6 col-md-auto px-5">
          <div className="e-checkbox font-size-sm">
            <label htmlFor={COMMENT_ID}>
              <input
                id={COMMENT_ID}
                type="checkbox"
                className="mr-1"
                onChange={handleFilters}
                defaultChecked={true}
                data-cy="ride-book-rides-info-comments"
              />
              <span>
                {f({ id: 'tripsOverview.modal.comment' })} ({handleCheckBoxCount(COMMENT_ID)})
              </span>
            </label>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-6">
          {loader && <Loading text={f({ id: 'waypoints.loading' })} />}
          {modalRecords && modalRecords.length > 0 && (
            <InfoTable
              firstData={modalRecords}
              count={recordsCount}
              currentPage={currentPage}
              start={props.start}
              rideId={props.rideId}
              customerId={props.customerId}
              filters={filters}
              information={information}
              isAnonym={props.isAnonym}
            />
          )}
        </div>
        <div className="col-12 col-md-6 mt-6 mt-md-0">
          <div className="b-table-list-modal-content">
            <div className="text-gray-light fs-1 font-weight-bold ml-2 mb-2">
              {f({ id: 'table.rideBook.modal.ToRide' })}
            </div>
            {!props.rideComment && props.rideTags.length === 0 && (
              <div className="ml-2">{f({ id: 'table.rideBook.modal.nothing' })}</div>
            )}
            <table className="table table-striped table-bordered m-0 table-export-knihy-jazd">
              <tbody>
                {getRideComment() && (
                  <tr key={`rideComment-${props.rideId}`}>
                    <td>
                      <Comment
                        comment={props.rideComment}
                        rideId={props.rideId}
                        customerId={props.customerId}
                        isEditable={getCommentEditPermission(props.customerId, props.locked)}
                        isRemovable={getCommentRemovePermisson(props.customerId, props.locked)}
                        isModal={true}
                      />
                    </td>
                  </tr>
                )}
                {getRideTags() &&
                  props.rideTags.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <Tag
                          name={item.name || ''}
                          rideId={props.rideId}
                          customerId={props.customerId}
                          isRemovable={getUsageRemovePermission(props.customerId, props.locked)}
                          isModal={true}
                          id={item.id}
                          color={item.color}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <div className="text-gray-light fs-1 font-weight-bold ml-2 mb-2 mt-5">
              {f({ id: 'table.rideBook.modal.toParking' })}
            </div>
            {!props.parkingComment && props.parkingTags.length === 0 && (
              <div className="ml-2">{f({ id: 'table.rideBook.modal.parking.nothing' })}</div>
            )}
            <table className="table table-striped table-bordered m-0 table-export-knihy-jazd">
              <tbody>
                {getParkingComment() && (
                  <tr key={`rideComment-${props.rideId}`}>
                    <td>
                      <Comment
                        comment={props.parkingComment}
                        rideId={props.rideId}
                        customerId={props.customerId}
                        isEditable={getCommentEditPermission(props.customerId, props.locked)}
                        isRemovable={getCommentRemovePermisson(props.customerId, props.locked)}
                        isModal={true}
                        isParking={true}
                      />
                    </td>
                  </tr>
                )}
                {getParkingTags() &&
                  props.parkingTags.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <Tag
                          name={item.name || ''}
                          rideId={props.rideId}
                          customerId={props.customerId}
                          isRemovable={getUsageRemovePermission(props.customerId, props.locked)}
                          isModal={true}
                          isParking={true}
                          id={item.id}
                          color={item.color}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </CmdModal>
  );
}

export default React.memo(InfoTableModal);
